import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import RecommendationCard from '../components/recommendations/RecommendationCard';
import ChallengeCard from '../components/player/ChallengeCard';
import EmptyState from '../components/ui/EmptyState';
import { SkeletonCard } from '../components/ui/Skeleton';
import './StudentPractice.css';

// Practice as a "play" area, not an exam portal — separates learning/play
// (Quick Play, Skill Practice, Timed Challenge, Mistake Recovery, all
// unranked/no-stakes) from official assessments (their own dashed-border,
// muted-icon card style — see ChallengeCard's `official` prop). Every entry
// point below reuses the existing, unchanged mock-test setup/take flow and
// assigned-tests flow — this page only changes how a student gets there.
const StudentPractice = () => {
  const navigate = useNavigate();
  const [recommendations, setRecommendations] = useState(null);
  const [pendingCount, setPendingCount] = useState(null);
  const [mistakeStats, setMistakeStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [recRes, testsRes, mistakesRes] = await Promise.allSettled([
          API.get('/portal/recommendations'),
          API.get('/portal/tests'),
          API.get('/mistakes'),
        ]);
        if (recRes.status === 'fulfilled') setRecommendations(recRes.value.data.recommendations || []);
        if (testsRes.status === 'fulfilled') {
          setPendingCount(testsRes.value.data.filter(t => t.attempt_status !== 'submitted').length);
        }
        if (mistakesRes.status === 'fulfilled') setMistakeStats(mistakesRes.value.data.stats || null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const topRec = recommendations && recommendations[0];

  return (
    <div className="z-page practice-body">
      <section className="z-section" aria-labelledby="pr-today">
        <h2 className="z-section-title" id="pr-today">Today's challenge</h2>
        {loading ? (
          <SkeletonCard lines={2} />
        ) : topRec ? (
          <RecommendationCard rec={topRec} variant="hero" />
        ) : (
          <EmptyState
            icon="rocket" compact
            title="Your first challenge is waiting"
            body="Start with a quick session and Zaptude will learn what you're good at."
            action={{ label: 'Start Quick Play', onClick: () => navigate('/student/mock', { state: { preselectFlow: 'quick' } }) }}
          />
        )}
      </section>

      <section className="z-section" aria-labelledby="pr-play">
        <h2 className="z-section-title" id="pr-play">Choose a challenge</h2>
        <div className="practice-challenge-list">
          <ChallengeCard
            icon="energy" art
            title="Quick Play"
            subtitle="A short, fast session to warm up"
            effort="~10 questions"
            onStart={() => navigate('/student/mock', { state: { preselectFlow: 'quick' } })}
          />
          <ChallengeCard
            icon="skill" art
            title="Skill Practice"
            subtitle="Practice one topic at a time"
            effort="You choose the topics"
            onStart={() => navigate('/student/mock', { state: { preselectFlow: 'category' } })}
          />
          <ChallengeCard
            icon="timer" art
            title="Timed Challenge"
            subtitle="Speed and accuracy, against the clock"
            effort="You choose the difficulty"
            onStart={() => navigate('/student/mock', { state: { preselectFlow: 'difficulty' } })}
          />
          <ChallengeCard
            icon="mistake" art
            title="Mistake Recovery"
            subtitle="Revisit questions you got wrong before"
            stat={mistakeStats ? `${mistakeStats.active} to recover` : undefined}
            tone={mistakeStats?.active > 0 ? 'urgent' : 'default'}
            onStart={() => navigate('/student/mistakes')}
          />
        </div>
      </section>

      <section className="z-section" aria-labelledby="pr-official">
        <h2 className="z-section-title" id="pr-official">Official assessments</h2>
        <div className="practice-challenge-list">
          <ChallengeCard
            official
            icon="test"
            title="Assigned by your college"
            subtitle="Timed, graded tests your placement cell scheduled"
            stat={pendingCount > 0 ? `${pendingCount} pending` : undefined}
            onStart={() => navigate('/student/tests')}
          />
        </div>
      </section>
    </div>
  );
};

export default StudentPractice;
